import AuthenticationLayout from '@/features/auth/components/AuthenticationLayout';
import { Trans } from '@lingui/react/macro';
import EmailLoginForm, {
  EmailLoginFormValues,
} from '@/features/auth/components/LoginOptions/EmailOTPLogin/EmailLoginForm';
import { useState } from 'react';
import { Paragraph } from '@wunder-mobility/ui-components';
import { Link } from 'react-router';
import {
  useRequestCreateOrSignInWithEmailMutation,
  useRequestEmailLoginMutation,
} from '@/features/auth/services/Auth.service';
import { generateSignatureHash } from '@/features/auth/utils/generateSignatureHash';
import { useRequestResolvedBranchQuery } from '@/features/branches/services/Branches.service';
import useAfterAuthPathResolver from '@/features/auth/hooks/useAfterAuthPathResolver';
import useMailtoLink from '@/core/hooks/useMailtoLink';
import { ApiError } from '@/core/types';
import { useRequestUserUpdateMutation } from '@/features/account/services/Account.service';
import EmailLoginVerificationForm from '@/features/auth/components/LoginOptions/EmailOTPLogin/EmailLoginVerificationForm';

const EmailAuthentication = () => {
  const [waitingForCode, setWaitingForCode] = useState(false);
  const [updateUser] = useRequestUserUpdateMutation();
  const [email, setEmail] = useState('');
  const resolvePath = useAfterAuthPathResolver();
  const { data: resolvedBranch } = useRequestResolvedBranchQuery();
  const [createOrSignup] = useRequestCreateOrSignInWithEmailMutation();
  const [login, { error: loginError }] = useRequestEmailLoginMutation();
  const error = loginError as ApiError;
  const { mailto } = useMailtoLink();
  const [newsletterSelection, setNewsletterSelection] = useState<Record<string, boolean>>({});

  const handleCreateCode = async ({
    email: userEmail,
    newsletterOptions,
  }: EmailLoginFormValues) => {
    if (resolvedBranch) {
      const { timestamp, signature } = generateSignatureHash(userEmail);
      createOrSignup({
        email: userEmail,
        branchId: String(resolvedBranch.id),
        timestamp,
        signature,
      })
        .unwrap()
        .then(() => {
          setNewsletterSelection(newsletterOptions);
          setWaitingForCode(true);
        })
        .catch((err) => {
          console.error(err);
        });
    }
    setEmail(userEmail);
  };

  const handleLogin = (code: string) => {
    login({ email, code })
      .unwrap()
      .then(() => {
        updateUser({ newsletterOptions: newsletterSelection });
        resolvePath();
      })
      .catch(void 0);
  };

  return (
    <AuthenticationLayout
      title={
        waitingForCode ? (
          <Trans id="auth.emailLoginCode.pageTitle">Confirm your e-mail address</Trans>
        ) : (
          <Trans id="auth.emailLoginForm.pageTitle">Welcome</Trans>
        )
      }
      alignCenter
      form={
        waitingForCode ? (
          <EmailLoginVerificationForm
            onResendCode={handleCreateCode}
            email={email}
            login={handleLogin}
            error={error}
            countdown={120}
          />
        ) : (
          <EmailLoginForm onSubmit={handleCreateCode} branchId={resolvedBranch?.id} />
        )
      }
      footer={
        waitingForCode && (
          <Paragraph textColor="text-400" size={5}>
            <Trans id="auth.emailLoginCode.footer">Still having trouble?</Trans>{' '}
            <Link to={mailto}>
              <Trans id="auth.emailLoginCode.contactSupport">Contact support</Trans>
            </Link>
          </Paragraph>
        )
      }
    />
  );
};

export default EmailAuthentication;
